// Master data libraries (V03-V07, design flag 4). One component serves the organisation library and a project's view.
import { useMutation, useQuery } from "convex/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Lock, Plus, Star, Trash2 } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, Checkbox, Empty, Field, Input, Modal, PageHeader, Select, Spinner, Tabs, cn, useRun, useToast } from "@/components/ui";
import { DataGrid, type Col } from "@/components/DataGrid";
import { ExportButton, ImportButton } from "@/components/Importer";
import { checkChain, formatCum, parseCum } from "@/engine/chains";
import { exportChains, parseChains } from "@/lib/formats";
import { MappedImportButton } from "@/components/ColumnMapper";
import { DOCTYPE_FIELDS, MILESTONE_FIELDS, REF_FIELDS } from "@/lib/importSchemas";
import { num, str, type MappedRow } from "@/lib/mapping";
import type { Preview } from "@/components/Importer";

type Tab = "chains" | "milestones" | "docTypes" | "refs";
type Kind = "milestones" | "chains" | "docTypes";

/** Phases offered for milestones, chains and document types (Cesar, Oct 16). Other values typed in are kept too. */
const LIB_CATEGORIES = ["Engineering", "Procurement", "Construction", "Commissioning"];
const categoryOptions = (used: (string | undefined)[]) => [...LIB_CATEGORIES, ...[...new Set(used.filter((c): c is string => !!c && !LIB_CATEGORIES.includes(c)))].sort()];

/** The signed-in user's starred items. Starred items list first everywhere in the library. */
function useFavs() {
  const list = useQuery(api.library.favorites);
  const toggle = useMutation(api.library.toggleFavorite);
  const set = useMemo(() => new Set(list ?? []), [list]);
  return { has: (kind: Kind, code: string) => set.has(`${kind}:${code}`), toggle: (kind: Kind, code: string) => void toggle({ kind, code }) };
}

function StarButton({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" aria-label={label} aria-pressed={on} title={on ? "Starred: lists first. Click to unstar" : "Star to list it first"} onClick={onClick} className="rounded p-0.5 hover:bg-slate-100">
      <Star className={cn("size-4", on ? "fill-amber-400 text-amber-500" : "text-slate-400")} />
    </button>
  );
}

type SortKey = "code" | "title" | "category" | "steps";
const SORT_LABEL: Record<SortKey, string> = { code: "Sort by code", title: "Sort by title", category: "Sort by category", steps: "Sort by number of steps" };

/** Starred first, then by the chosen column (numbers in codes sort naturally). */
function sortLib<T extends { code: string; title?: string; category?: string; steps?: unknown[] }>(rows: T[], key: SortKey, starred: (r: T) => boolean): T[] {
  const val = (r: T) => (key === "steps" ? String(r.steps?.length ?? 0).padStart(3, "0") : key === "code" ? r.code : (r[key] ?? "\uffff"));
  return [...rows].sort((a, b) => Number(starred(b)) - Number(starred(a)) || val(a).localeCompare(val(b), undefined, { numeric: true }) || a.code.localeCompare(b.code, undefined, { numeric: true }));
}

/** Search, category, sort and "starred only" controls shared by the library tabs. */
function ListTools({
  q,
  setQ,
  cat,
  setCat,
  cats,
  sort,
  setSort,
  sorts,
  onlyFav,
  setOnlyFav,
  placeholder,
  children,
}: {
  q: string;
  setQ: (v: string) => void;
  cat: string;
  setCat: (v: string) => void;
  cats: string[];
  sort: SortKey;
  setSort: (v: SortKey) => void;
  sorts: SortKey[];
  onlyFav: boolean;
  setOnlyFav: (v: boolean) => void;
  placeholder: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} className="w-64" aria-label={placeholder} />
      <Select value={cat} onChange={(e) => setCat(e.target.value)} className="w-44" aria-label="Category">
        <option value="">All categories</option>
        {cats.map((c) => (
          <option key={c}>{c}</option>
        ))}
        <option value="-">No category</option>
      </Select>
      {children}
      <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="w-52" aria-label="Sort">
        {sorts.map((k) => (
          <option key={k} value={k}>
            {SORT_LABEL[k]}
          </option>
        ))}
      </Select>
      <Checkbox label="Starred only" checked={onlyFav} onChange={setOnlyFav} />
    </div>
  );
}
const catMatch = (cat: string, c: string | undefined) => !cat || (cat === "-" ? !c : c === cat);

export function LibraryPage() {
  const { tab } = useSearch({ from: "/library" });
  const nav = useNavigate();
  const me = useQuery(api.account.me);
  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader title="Organisation library" sub="Milestones, chains and document types every project inherits. Projects can customize a copy." />
      <LibraryView tab={tab as Tab} onTab={(t) => void nav({ to: "/library", search: { tab: t } })} canEdit={me?.role === "admin"} />
    </div>
  );
}

export function LibraryView({ projectId, tab, onTab, canEdit }: { projectId?: Id<"projects">; tab: Tab; onTab: (t: Tab) => void; canEdit: boolean }) {
  const lib = useQuery(api.library.list, { projectId });
  const refs = useQuery(api.library.refs, projectId ? "skip" : {});
  const loadDefaults = useMutation(api.library.loadDefaults);
  const run = useRun();
  const toast = useToast();
  if (!lib) return <Spinner />;
  // Milestones first: chains are built from them (Cesar, Oct 16).
  const items = [
    { value: "milestones" as Tab, label: `Milestones (${lib.milestones.length})` },
    { value: "chains" as Tab, label: `Milestone chains (${lib.chains.length})` },
    { value: "docTypes" as Tab, label: `Document types (${lib.docTypes.length})` },
    ...(projectId ? [] : [{ value: "refs" as Tab, label: `Reference deliverables${refs ? ` (${refs.length})` : ""}` }]),
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={tab} onChange={onTab} items={items} />
        {!projectId && canEdit && (
          <Button
            onClick={async () => {
              const r = await run(() => loadDefaults({}));
              if (r)
                toast({
                  text: `Loaded ${r.added} chains from the default library.${r.skipped.length ? `\nSkipped (need fixing in the source): ${r.skipped.join("; ")}` : ""}`,
                  tone: r.skipped.length ? "info" : "success",
                });
            }}
          >
            Load default library
          </Button>
        )}
      </div>
      {projectId && <ProjectScope projectId={projectId} lib={lib} kind={tab === "refs" ? "milestones" : tab} canEdit={canEdit} />}
      {tab === "chains" && <ChainsTab projectId={projectId} lib={lib} canEdit={canEdit} />}
      {tab === "milestones" && <MilestonesTab projectId={projectId} lib={lib} canEdit={canEdit} />}
      {tab === "docTypes" && <DocTypesTab projectId={projectId} lib={lib} canEdit={canEdit} />}
      {tab === "refs" && !projectId && (refs ? <RefsTab lib={lib} refs={refs} canEdit={canEdit} /> : <Spinner />)}
    </div>
  );
}

/**
 * A project uses either the whole organisation library or a list picked from it (Cesar, Oct 16: "pull from REF").
 * Items the project uses always stay; project-only and customized items are the project's own.
 */
function ProjectScope({ projectId, lib, kind, canEdit }: { projectId: Id<"projects">; lib: Lib; kind: Kind; canEdit: boolean }) {
  const setScope = useMutation(api.library.setLibraryScope);
  const run = useRun();
  const [pulling, setPulling] = useState(false);
  const total = lib.libraryCounts;
  const shown = (k: Kind) => lib[k].filter((x) => x.source !== "project").length;
  const LABEL: Record<Kind, string> = { milestones: "milestones", chains: "chains", docTypes: "document types" };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-surface p-3 text-sm">
      <span className="text-slate-600">
        {lib.picked ? (
          <>
            This project uses <b>items picked from the organisation library</b>: {shown("milestones")} of {total.milestones} milestones, {shown("chains")} of {total.chains} chains, {shown("docTypes")} of {total.docTypes} document types.
          </>
        ) : (
          <>
            This project uses the <b>whole organisation library</b> ({total.milestones} milestones, {total.chains} chains, {total.docTypes} document types).
          </>
        )}
      </span>
      {canEdit && (
        <span className="flex gap-2">
          {lib.picked && (
            <Button size="sm" variant="ghost" onClick={() => window.confirm("Show the whole organisation library in this project again? Your picks are forgotten.") && run(() => setScope({ projectId, picked: false }), "This project uses the whole library again")}>
              Use the whole library
            </Button>
          )}
          <Button size="sm" variant="primary" onClick={() => setPulling(true)}>
            Pull {LABEL[kind]} from library
          </Button>
        </span>
      )}
      {pulling && <PullDialog projectId={projectId} lib={lib} kind={kind} onClose={() => setPulling(false)} />}
    </div>
  );
}

function PullDialog({ projectId, lib, kind, onClose }: { projectId: Id<"projects">; lib: Lib; kind: Kind; onClose: () => void }) {
  const org = useQuery(api.library.list, {});
  const pick = useMutation(api.library.pickFromLibrary);
  const setScope = useMutation(api.library.setLibraryScope);
  const run = useRun();
  const favs = useFavs();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [onlyFav, setOnlyFav] = useState(false);
  const [sort, setSort] = useState<SortKey>("code");
  // In use: a chain a deliverable uses, a milestone a shown chain uses, a doc type a deliverable has. Those can't be removed.
  const inUse = useMemo(() => {
    if (kind === "chains") return new Set(Object.keys(lib.usage));
    if (!lib.picked) return new Set<string>();
    return new Set(kind === "milestones" ? lib.picked.milestonesInUse : lib.picked.docTypesInUse);
  }, [lib, kind]);
  const own = useMemo(() => new Set(lib[kind].filter((x) => x.source !== "library").map((x) => x.code)), [lib, kind]);
  const initial = useMemo(() => new Set(lib.picked ? lib[kind].filter((x) => x.source === "library").map((x) => x.code) : []), [lib, kind]);
  const [sel, setSel] = useState<Set<string>>(initial);
  if (!org) return null;
  const all = org[kind] as { code: string; title?: string; category?: string; steps?: { milestone: string }[] }[];
  const cats = categoryOptions(all.map((x) => x.category));
  const rows = sortLib(
    all.filter((x) => (!q || `${x.code} ${x.title ?? ""}`.toLowerCase().includes(q.toLowerCase())) && catMatch(cat, x.category) && (!onlyFav || favs.has(kind, x.code))),
    sort,
    (x) => favs.has(kind, x.code),
  );
  const locked = (c: string) => inUse.has(c) || own.has(c);
  const added = [...sel].filter((c) => !initial.has(c));
  const removed = [...initial].filter((c) => !sel.has(c) && !locked(c));
  const LABEL: Record<Kind, string> = { milestones: "milestones", chains: "chains", docTypes: "document types" };
  const toggle = (c: string, on: boolean) => setSel((s) => {
    const n = new Set(s);
    if (on) n.add(c);
    else n.delete(c);
    return n;
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Pull ${LABEL[kind]} from the organisation library`}
      wide="xl"
      footer={
        <>
          <span className="mr-auto self-center text-xs text-slate-500">
            {added.length} to add, {removed.length} to remove
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!added.length && !removed.length && !!lib.picked}
            onClick={async () => {
              const ok = await run(async () => {
                if (!lib.picked) await setScope({ projectId, picked: true });
                if (added.length) await pick({ projectId, kind, codes: added });
                if (removed.length) await pick({ projectId, kind, codes: removed, remove: true });
                return true;
              }, `${added.length ? `Added ${added.length}` : ""}${added.length && removed.length ? ", " : ""}${removed.length ? `removed ${removed.length}` : ""}${!added.length && !removed.length ? "This project now uses a picked list" : ""}`);
              if (ok) onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {!lib.picked && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
          This project shows the whole organisation library now. Saving switches it to a <b>picked list</b>: only what you tick here, plus what the project already uses, shows in this project. You can switch back with "Use the whole library".
        </p>
      )}
      <ListTools q={q} setQ={setQ} cat={cat} setCat={setCat} cats={cats} sort={sort} setSort={setSort} sorts={kind === "chains" ? ["code", "category", "steps"] : ["code", "title", "category"]} onlyFav={onlyFav} setOnlyFav={setOnlyFav} placeholder="Search code or title" />
      <div className="mt-2 flex items-center gap-3 text-xs">
        <button type="button" className="text-brand-700 underline" onClick={() => setSel((s) => new Set([...s, ...rows.map((r) => r.code)]))}>
          Tick all {rows.length} shown
        </button>
        <button type="button" className="text-slate-600 underline" onClick={() => setSel((s) => new Set([...s].filter((c) => !rows.some((r) => r.code === c))))}>
          Untick all shown
        </button>
      </div>
      <div className="mt-2 max-h-[55vh] overflow-y-auto rounded border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="w-10 px-2 py-1.5" />
              <th className="px-2 py-1.5">Code</th>
              <th className="px-2 py-1.5">{kind === "chains" ? "Steps" : "Title"}</th>
              <th className="px-2 py-1.5">Category</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.code} className="border-t border-slate-100">
                <td className="px-2 py-1">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-600"
                    aria-label={`Use ${x.code} in this project`}
                    checked={sel.has(x.code) || locked(x.code)}
                    disabled={locked(x.code)}
                    onChange={(e) => toggle(x.code, e.target.checked)}
                  />
                </td>
                <td className="px-2 py-1 font-medium">
                  {favs.has(kind, x.code) && <Star className="mr-1 inline size-3.5 fill-amber-400 text-amber-500" />}
                  {x.code}
                  {inUse.has(x.code) && <Badge className="ml-1">in use</Badge>}
                  {own.has(x.code) && <Badge tone="amber" className="ml-1">customized</Badge>}
                </td>
                <td className="px-2 py-1 text-slate-600">{kind === "chains" ? x.steps?.map((s) => s.milestone).join(" / ") : x.title}</td>
                <td className="px-2 py-1 text-slate-600">{x.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <Empty>Nothing matches.</Empty>}
      </div>
    </Modal>
  );
}

type Lib = NonNullable<ReturnType<typeof useLib>>;
function useLib() {
  return useQuery(api.library.list, {});
}

const sourceBadge = (s: string) =>
  s === "override" ? (
    <Badge tone="amber" title="Customized for this project; differs from the organisation library">differs</Badge>
  ) : s === "project" ? (
    <Badge tone="violet">project only</Badge>
  ) : null;

function ChainsTab({ projectId, lib, canEdit }: { projectId?: Id<"projects">; lib: Lib; canEdit: boolean }) {
  const [edit, setEdit] = useState<null | { code: string; title?: string; category?: string; steps: { milestone: string; cum: number; mfgCum?: number }[]; isNew: boolean }>(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [nSteps, setNSteps] = useState("");
  const [sort, setSort] = useState<SortKey>("code");
  const [onlyFav, setOnlyFav] = useState(false);
  const favs = useFavs();
  const importRows = useMutation(api.library.importRows);
  const customize = useMutation(api.library.customize);
  const del = useMutation(api.library.deleteChain);
  const run = useRun();
  const known = useMemo(() => new Set(lib.milestones.map((m) => m.code)), [lib]);
  const cats = categoryOptions(lib.chains.map((c) => c.category));
  const stepCounts = [...new Set(lib.chains.map((c) => c.steps.length))].sort((a, b) => a - b);
  const rows = sortLib(
    lib.chains.filter(
      (c) =>
        (!q || `${c.code} ${c.title ?? ""} ${c.steps.map((s) => s.milestone).join(" ")}`.toLowerCase().includes(q.toLowerCase())) &&
        catMatch(cat, c.category) &&
        (!nSteps || c.steps.length === Number(nSteps)) &&
        (!onlyFav || favs.has("chains", c.code)),
    ),
    sort,
    (c) => favs.has("chains", c.code),
  );
  return (
    <Card
      title="Milestone chains"
      actions={
        <>
          <ExportButton file={`chains${projectId ? "-project" : ""}.xlsx`} sheets={() => [exportChains(lib.chains)]} />
          {canEdit && (
            <ImportButton
              build={(sheets) => {
                const { chains, errors } = parseChains(sheets[0].rows);
                const existing = new Map(lib.chains.map((c) => [c.code, c]));
                return {
                  problems: errors.map((e) => `Row ${e.line}: ${e.message}`),
                  rows: chains.map((c, i) => {
                    const issues = checkChain({ code: c.code, steps: c.steps }, known);
                    const ex = existing.get(c.code);
                    const locked = lib.usage[c.code]?.earned;
                    const same = ex && JSON.stringify(ex.steps) === JSON.stringify(c.steps);
                    return {
                      line: i + 1,
                      key: c.code,
                      status: issues.length ? "error" : !ex ? "new" : same ? "unchanged" : locked ? "error" : "changed",
                      detail: issues.length
                        ? issues.map((x) => ("milestone" in x ? `${x.kind} ${x.milestone}` : "step" in x ? `${x.kind} at step ${x.step}` : x.kind)).join(", ")
                        : ex && !same && locked
                          ? "Chain has earned progress and is locked"
                          : c.steps.map((s) => `${s.milestone} ${formatCum(s)}`).join(" / "),
                    };
                  }),
                  commit: () => {
                    const good = chains.filter((c) => !checkChain(c, known).length && JSON.stringify(existing.get(c.code)?.steps) !== JSON.stringify(c.steps));
                    return importRows({ projectId, chains: good });
                  },
                };
              }}
            />
          )}
          {canEdit && (
            <Button variant="primary" onClick={() => setEdit({ code: "", steps: [], isNew: true })}>
              <Plus className="size-4" /> New chain
            </Button>
          )}
        </>
      }
      bodyClass="p-0"
    >
      <div className="border-b border-slate-100 p-3">
        <ListTools q={q} setQ={setQ} cat={cat} setCat={setCat} cats={cats} sort={sort} setSort={setSort} sorts={["code", "category", "steps"]} onlyFav={onlyFav} setOnlyFav={setOnlyFav} placeholder="Search code, title or milestone">
          {/* Cesar, Oct 16: filter by number of steps. */}
          <Select value={nSteps} onChange={(e) => setNSteps(e.target.value)} className="w-36" aria-label="Number of steps">
            <option value="">Any steps</option>
            {stepCounts.map((n) => (
              <option key={n} value={n}>
                {n} steps
              </option>
            ))}
          </Select>
        </ListTools>
        <p className="mt-2 text-xs text-slate-500">
          {rows.length} of {lib.chains.length} chains shown.
        </p>
      </div>
      {!rows.length ? (
        <div className="p-4">
          <Empty>{lib.chains.length ? "No chains match." : "No chains yet. Load the default library or import your chain definitions."}</Empty>
        </div>
      ) : (
        <div className="max-h-[65vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="w-8 py-2 pl-3" aria-label="Starred" />
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Steps (cumulative %)</th>
                <th className="px-4 py-2">Used</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const u = lib.usage[c.code];
                const issues = checkChain({ code: c.code, steps: c.steps }, known);
                return (
                  <tr key={c._id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                    <td className="py-2 pl-3">
                      <StarButton on={favs.has("chains", c.code)} onClick={() => favs.toggle("chains", c.code)} label={`Star ${c.code}`} />
                    </td>
                    <td className="px-4 py-2 font-medium">
                      {c.code} {sourceBadge(c.source)}
                      {c.title && <div className="text-xs font-normal text-slate-500">{c.title}</div>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{c.category}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {c.steps.map((s, i) => (
                          <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                            {s.milestone} <span className="text-slate-500">{formatCum(s)}</span>
                          </span>
                        ))}
                      </div>
                      {issues.length > 0 && <div className="mt-1 text-xs text-red-600">Invalid: {issues.map((x) => ("step" in x ? `${x.kind} at step ${x.step}` : x.kind)).join(", ")}</div>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {u ? `${u.count}` : ""}
                      {u?.earned && (
                        <span title="Has earned progress: read-only. Create a new chain code to change it." className="ml-1 inline-flex text-amber-600">
                          <Lock className="size-3.5" />
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {canEdit && projectId && c.source === "library" && (
                        <Button size="sm" variant="ghost" onClick={() => run(() => customize({ projectId, kind: "chains", code: c.code }), `${c.code} copied into this project`)}>
                          Customize
                        </Button>
                      )}
                      {canEdit && (!projectId || c.source !== "library") && (
                        <>
                          <Button size="sm" variant="ghost" disabled={u?.earned} onClick={() => setEdit({ code: c.code, title: c.title, category: c.category, steps: c.steps, isNew: false })}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Delete ${c.code}`}
                            onClick={() => run(() => del({ id: c._id }), c.source === "override" ? `Reverted ${c.code} to the library version` : `Deleted ${c.code}`)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit && <ChainEditor projectId={projectId} init={edit} lib={lib} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function ChainEditor({
  projectId,
  init,
  lib,
  onClose,
}: {
  projectId?: Id<"projects">;
  init: { code: string; title?: string; category?: string; steps: { milestone: string; cum: number; mfgCum?: number }[]; isNew: boolean };
  lib: Lib;
  onClose: () => void;
}) {
  const save = useMutation(api.library.upsertChain);
  const run = useRun();
  const favs = useFavs();
  const milestones = lib.milestones;
  // Shorten the milestone list by phase (Cesar, Oct 16): a milestone matches its own category, or a chain of that
  // category that already uses it. Starred milestones list first.
  const [msCat, setMsCat] = useState(init.category && categoryOptions(lib.milestones.map((m) => m.category)).includes(init.category) ? init.category : "");
  const usedIn = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const c of lib.chains) if (c.category) for (const s of c.steps) (m.get(s.milestone) ?? m.set(s.milestone, new Set()).get(s.milestone)!).add(c.category);
    return m;
  }, [lib.chains]);
  const msOptions = sortLib(
    milestones.filter((m) => !msCat || m.category === msCat || usedIn.get(m.code)?.has(msCat)),
    "code",
    (m) => favs.has("milestones", m.code),
  );
  const [code, setCode] = useState(init.code);
  const [title, setTitle] = useState(init.title ?? "");
  const [category, setCategory] = useState(init.category ?? "");
  const [steps, setSteps] = useState(init.steps.length ? init.steps.map((s) => ({ milestone: s.milestone, val: formatCum(s) })) : [{ milestone: "", val: "" }]);
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };
  const parsed = steps.map((s) => ({ milestone: s.milestone, p: parseCum(s.val) }));
  const valid = parsed.every((s) => s.p && s.milestone);
  const chain = { code, steps: parsed.map((s) => ({ milestone: s.milestone, cum: s.p?.cum ?? NaN, ...(s.p?.mfgCum !== undefined ? { mfgCum: s.p.mfgCum } : {}) })) };
  const issues = valid ? checkChain(chain, new Set(milestones.map((m) => m.code))) : [];
  return (
    <Modal
      open
      onClose={onClose}
      title={init.isNew ? "New chain" : `Edit ${init.code}`}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!code || !valid || issues.length > 0}
            onClick={async () => {
              const ok = await run(() => save({ projectId, code, title: title || undefined, category: category || undefined, steps: chain.steps }), `Saved ${code.toUpperCase()}`);
              if (ok !== undefined) onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <Field label="Code">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!init.isNew} />
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Category">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Engineering, Procurement..." list="lib-categories" />
          <datalist id="lib-categories">
            {categoryOptions(lib.chains.map((c) => c.category)).map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <span className="text-sm text-slate-600">Milestone list:</span>
        <Select value={msCat} onChange={(e) => setMsCat(e.target.value)} className="w-52" aria-label="Milestone category">
          <option value="">All categories ({milestones.length})</option>
          {categoryOptions(milestones.map((m) => m.category).concat(lib.chains.map((c) => c.category))).map((c) => (
            <option key={c}>{c}</option>
          ))}
        </Select>
        <span className="text-xs text-slate-500">{msOptions.length} milestones, starred first</span>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Enter the <b>cumulative</b> % at each step (the running total, e.g. 10, 30, 70, 100), ending at 100%. The step column shows what each step adds. For procurement, add the manufacturing % in brackets, e.g. 40%[20%].
      </p>
      <table className="mt-2 w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="w-10 py-1">#</th>
            <th className="py-1">Milestone</th>
            <th className="w-36 py-1">Cumulative %</th>
            <th className="w-16 py-1 text-right">Step adds</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {steps.map((s, i) => (
            <tr key={i}>
              <td className="py-1 text-slate-500">{i + 1}</td>
              <td className="py-1 pr-2">
                <Select aria-label={`Milestone ${i + 1}`} value={s.milestone} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, milestone: e.target.value } : x)))}>
                  <option value="">Pick a milestone</option>
                  {(s.milestone && !msOptions.some((m) => m.code === s.milestone) ? [...milestones.filter((m) => m.code === s.milestone), ...msOptions] : msOptions).map((m) => (
                    <option key={m.code} value={m.code}>
                      {favs.has("milestones", m.code) ? "★ " : ""}
                      {m.code} · {m.title}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="py-1 pr-2">
                <Input aria-label={`Cumulative ${i + 1}`} value={s.val} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, val: e.target.value } : x)))} className={parseCum(s.val) ? "" : "border-red-400"} />
              </td>
              <td className="num py-1 text-right text-xs text-slate-500">
                {(() => {
                  const c = parseCum(s.val)?.cum;
                  const prev = i > 0 ? parseCum(steps[i - 1].val)?.cum : 0;
                  return c !== undefined && prev !== undefined ? `${+((c - prev) * 100).toFixed(2)}%` : "";
                })()}
              </td>
              <td className="whitespace-nowrap text-right">
                <Button size="sm" variant="ghost" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                  ↑
                </Button>
                <Button size="sm" variant="ghost" aria-label="Move down" onClick={() => move(i, 1)} disabled={i === steps.length - 1}>
                  ↓
                </Button>
                <Button size="sm" variant="ghost" aria-label="Remove step" onClick={() => setSteps(steps.filter((_, j) => j !== i))} disabled={steps.length === 1}>
                  <Trash2 className="size-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="sm" className="mt-2" onClick={() => setSteps([...steps, { milestone: "", val: "" }])}>
        <Plus className="size-3.5" /> Add step
      </Button>
      {issues.length > 0 && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {issues.map((x) => ("milestone" in x ? `${x.kind} ${x.milestone}` : "step" in x ? `${x.kind} at step ${x.step}` : x.kind)).join(", ")}
        </p>
      )}
    </Modal>
  );
}

function MilestonesTab({ projectId, lib, canEdit }: { projectId?: Id<"projects">; lib: Lib; canEdit: boolean }) {
  const upsert = useMutation(api.library.upsertMilestone);
  const importRows = useMutation(api.library.importRows);
  const fill = useMutation(api.library.fillMilestoneCategories);
  const run = useRun();
  const toast = useToast();
  const favs = useFavs();
  const [add, setAdd] = useState({ code: "", title: "", category: "" });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState<SortKey>("code");
  const [onlyFav, setOnlyFav] = useState(false);
  type M = Lib["milestones"][number];
  const cats = categoryOptions(lib.milestones.map((m) => m.category));
  const rows = sortLib(
    lib.milestones.filter((m) => (!q || `${m.code} ${m.title}`.toLowerCase().includes(q.toLowerCase())) && catMatch(cat, m.category) && (!onlyFav || favs.has("milestones", m.code))),
    sort,
    (m) => favs.has("milestones", m.code),
  );
  const ro = (r: M) => !!projectId && r.source === "library";
  const save = (r: M, p: { title?: string; active?: boolean; category?: string }) => void run(() => upsert({ projectId, code: r.code, title: r.title, active: r.active, ...p }));
  const cols: Col<M>[] = [
    // Drawn as a star; a click toggles it (onCellClick below).
    { id: "fav", title: "★", width: 40, get: (r) => (favs.has("milestones", r.code) ? "★" : "☆"), tone: (r) => (favs.has("milestones", r.code) ? "warn" : undefined) },
    { id: "code", title: "Code", width: 110, get: (r) => r.code },
    { id: "title", title: "Title", width: 360, get: (r) => r.title, set: canEdit ? (r, v) => save(r, { title: String(v ?? "") }) : undefined, readonly: ro },
    { id: "category", title: "Category", width: 130, options: cats, get: (r) => r.category, set: canEdit ? (r, v) => save(r, { category: v === null ? "" : String(v) }) : undefined, readonly: ro },
    { id: "active", title: "Active", width: 70, kind: "bool", get: (r) => r.active, set: canEdit ? (r, v) => save(r, { active: !!v }) : undefined, readonly: ro },
    { id: "src", title: "Source", width: 110, get: (r) => (r.source === "library" ? "library" : r.source === "override" ? "differs" : "project") },
  ];
  return (
    <Card
      title="Milestones"
      actions={
        <>
          {canEdit && !projectId && lib.milestones.some((m) => !m.category) && (
            <Button
              onClick={async () => {
                const n = await run(() => fill({}));
                if (n !== undefined) toast({ text: n ? `Filled the category of ${n} milestones from the chains that use them` : "No blank categories could be filled from the chains", tone: "info" });
              }}
              title="Sets each blank category to the category of the chains that use the milestone"
            >
              Fill categories from chains
            </Button>
          )}
          <ExportButton file="milestones.xlsx" sheets={() => [{ name: "Milestones", headerRow: 0, rows: [["Code", "Title", "Active", "Category"], ...lib.milestones.map((m) => [m.code, m.title, m.active ? "Y" : "N", m.category ?? null])] }]} />
          {canEdit && (
            <MappedImportButton
              fields={MILESTONE_FIELDS}
              rememberKey="milestones"
              build={(rows) => {
                const items = rows.map((r) => ({
                  line: r.line,
                  code: str(r.values.code).toUpperCase(),
                  title: str(r.values.title),
                  active: r.values.active === undefined || r.values.active === null ? undefined : ["Y", "YES", "TRUE", "1", "X"].includes(str(r.values.active).toUpperCase()),
                  category: r.values.category === undefined ? undefined : str(r.values.category),
                }));
                const ex = new Map(lib.milestones.map((m) => [m.code, m]));
                const same = (m: (typeof items)[number]) => ex.get(m.code)!.title === m.title && (m.category === undefined || (ex.get(m.code)!.category ?? "") === m.category);
                return {
                  problems: [],
                  rows: items.map((m) => ({
                    line: m.line,
                    key: m.code || "(blank)",
                    status: !m.code ? "error" : !ex.has(m.code) ? "new" : same(m) ? "unchanged" : "changed",
                    detail: m.code ? [m.title, m.category].filter(Boolean).join(" · ") : "No code",
                  })),
                  commit: () =>
                    importRows({
                      projectId,
                      milestones: items
                        .filter((m) => m.code && (!ex.has(m.code) || !same(m)))
                        .map(({ code, title, active, category }) => ({ code, title, ...(active === undefined ? {} : { active }), ...(category === undefined ? {} : { category }) })),
                    }),
                };
              }}
            />
          )}
        </>
      }
    >
      {canEdit && (
        <form
          className="mb-3 flex gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!add.code) return toast({ text: "Enter a code", tone: "error" });
            const ok = await run(() => upsert({ projectId, code: add.code, title: add.title, category: add.category }));
            if (ok !== undefined) setAdd({ code: "", title: "", category: add.category });
          }}
        >
          <Input placeholder="Code" aria-label="New milestone code" value={add.code} onChange={(e) => setAdd({ ...add, code: e.target.value.toUpperCase() })} className="w-32" />
          <Input placeholder="Title" aria-label="New milestone title" value={add.title} onChange={(e) => setAdd({ ...add, title: e.target.value })} className="w-96" />
          <Select aria-label="New milestone category" value={add.category} onChange={(e) => setAdd({ ...add, category: e.target.value })} className="w-44">
            <option value="">No category</option>
            {cats.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
          <Button type="submit">
            <Plus className="size-4" /> Add
          </Button>
        </form>
      )}
      <div className="mb-2">
        <ListTools q={q} setQ={setQ} cat={cat} setCat={setCat} cats={cats} sort={sort} setSort={setSort} sorts={["code", "title", "category"]} onlyFav={onlyFav} setOnlyFav={setOnlyFav} placeholder="Search code or title" />
        <p className="mt-2 text-xs text-slate-500">
          {rows.length} of {lib.milestones.length} milestones shown. Click ☆ to star a milestone: starred ones list first here and in the chain editor.
        </p>
      </div>
      <DataGrid
        rows={rows}
        cols={cols}
        height={520}
        onCellClick={(r, c) => c.id === "fav" && favs.toggle("milestones", r.code)}
        onEdit={(edits) => edits.forEach((e) => e.col.set?.(e.row, e.value))}
        onError={(m) => toast({ text: m, tone: "error" })}
      />
    </Card>
  );
}

function DocTypesTab({ projectId, lib, canEdit }: { projectId?: Id<"projects">; lib: Lib; canEdit: boolean }) {
  const upsert = useMutation(api.library.upsertDocType);
  const del = useMutation(api.library.deleteDocType);
  const importRows = useMutation(api.library.importRows);
  const run = useRun();
  const favs = useFavs();
  const [add, setAdd] = useState({ code: "", title: "", category: "" });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState<SortKey>("code");
  const [onlyFav, setOnlyFav] = useState(false);
  const cats = categoryOptions(lib.docTypes.map((d) => d.category));
  const rows = sortLib(
    lib.docTypes.filter((d) => (!q || `${d.code} ${d.title}`.toLowerCase().includes(q.toLowerCase())) && catMatch(cat, d.category) && (!onlyFav || favs.has("docTypes", d.code))),
    sort,
    (d) => favs.has("docTypes", d.code),
  );
  return (
    <Card
      title="Document types"
      actions={
        <>
          <ExportButton file="document-types.xlsx" sheets={() => [{ name: "Doc DLV Types", headerRow: 0, rows: [["Doc/DLV Type", "Description", "Category"], ...lib.docTypes.map((d) => [d.code, d.title, d.category ?? null])] }]} />
          {canEdit && (
            <MappedImportButton
              fields={DOCTYPE_FIELDS}
              rememberKey="docTypes"
              build={(rows) => {
                const items = rows.map((r) => ({ line: r.line, code: str(r.values.code).toUpperCase(), title: str(r.values.title), category: r.values.category === undefined ? undefined : str(r.values.category) }));
                const ex = new Map(lib.docTypes.map((d) => [d.code, d]));
                const same = (d: (typeof items)[number]) => ex.get(d.code)!.title === d.title && (d.category === undefined || (ex.get(d.code)!.category ?? "") === d.category);
                return {
                  problems: [],
                  rows: items.map((d) => ({
                    line: d.line,
                    key: d.code || "(blank)",
                    status: !d.code ? "error" : !ex.has(d.code) ? "new" : same(d) ? "unchanged" : "changed",
                    detail: d.code ? [d.title, d.category].filter(Boolean).join(" · ") : "No code",
                  })),
                  commit: () =>
                    importRows({
                      projectId,
                      docTypes: items.filter((d) => d.code && (!ex.has(d.code) || !same(d))).map(({ code, title, category }) => ({ code, title, ...(category === undefined ? {} : { category }) })),
                    }),
                };
              }}
            />
          )}
        </>
      }
    >
      {canEdit && (
        <form
          className="mb-3 flex gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await run(() => upsert({ projectId, ...add }));
            if (ok !== undefined) setAdd({ code: "", title: "", category: add.category });
          }}
        >
          <Input placeholder="Code (e.g. DWG)" aria-label="Doc type code" value={add.code} onChange={(e) => setAdd({ ...add, code: e.target.value.toUpperCase() })} className="w-40" />
          <Input placeholder="Title" aria-label="Doc type title" value={add.title} onChange={(e) => setAdd({ ...add, title: e.target.value })} className="w-80" />
          <Select aria-label="Doc type category" value={add.category} onChange={(e) => setAdd({ ...add, category: e.target.value })} className="w-44">
            <option value="">No category</option>
            {cats.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
          <Button type="submit" disabled={!add.code}>
            <Plus className="size-4" /> Add
          </Button>
        </form>
      )}
      <div className="mb-2">
        <ListTools q={q} setQ={setQ} cat={cat} setCat={setCat} cats={cats} sort={sort} setSort={setSort} sorts={["code", "title", "category"]} onlyFav={onlyFav} setOnlyFav={setOnlyFav} placeholder="Search code or title" />
        <p className="mt-2 text-xs text-slate-500">
          {rows.length} of {lib.docTypes.length} document types shown.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="w-8" aria-label="Starred" />
            <th className="w-32 py-1">Code</th>
            <th className="py-1">Title</th>
            <th className="w-48 py-1">Category</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const editable = canEdit && (!projectId || d.source !== "library");
            return (
              <tr key={d._id} className="border-t border-slate-100">
                <td className="py-1.5">
                  <StarButton on={favs.has("docTypes", d.code)} onClick={() => favs.toggle("docTypes", d.code)} label={`Star ${d.code}`} />
                </td>
                <td className="py-1.5 font-medium">{d.code}</td>
                <td className="py-1.5">
                  {d.title} {sourceBadge(d.source)}
                </td>
                <td className="py-1 pr-2">
                  {editable ? (
                    <Select aria-label={`Category of ${d.code}`} className="h-8" value={d.category ?? ""} onChange={(e) => run(() => upsert({ projectId, code: d.code, title: d.title, category: e.target.value }))}>
                      <option value="">No category</option>
                      {cats.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </Select>
                  ) : (
                    <span className="text-slate-600">{d.category}</span>
                  )}
                </td>
                <td className="py-1.5 text-right">
                  {editable && (
                    <Button size="sm" variant="ghost" aria-label={`Delete ${d.code}`} onClick={() => run(() => del({ id: d._id }))}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!rows.length && <Empty>{lib.docTypes.length ? "No document types match." : "No document types yet."}</Empty>}
    </Card>
  );
}

type Refs = NonNullable<ReturnType<typeof useRefs>>;
function useRefs() {
  return useQuery(api.library.refs, {});
}

function RefsTab({ lib, refs, canEdit }: { lib: Lib; refs: Refs; canEdit: boolean }) {
  const upsert = useMutation(api.library.upsertRef);
  const delMany = useMutation(api.library.deleteRefs);
  const importRefs = useMutation(api.library.importRefs);
  const run = useRun();
  const toast = useToast();
  type R = Refs[number];
  const chainCodes = lib.chains.map((c) => c.code);
  const setF = (r: R, k: keyof R, v: unknown) => {
    const { _id, _creationTime, tenantId, ...rest } = r;
    void _creationTime;
    void tenantId;
    return void run(() => upsert({ id: _id, ...rest, [k]: v ?? undefined } as Parameters<typeof upsert>[0]));
  };
  const cols: Col<R>[] = [
    { id: "discipline", title: "Discipline", width: 90, get: (r) => r.discipline, set: canEdit ? (r, v) => setF(r, "discipline", v === null ? undefined : String(v)) : undefined },
    { id: "docType", title: "Doc type", width: 80, get: (r) => r.docType, set: canEdit ? (r, v) => setF(r, "docType", String(v ?? "")) : undefined },
    { id: "engCode", title: "Eng. code", width: 100, get: (r) => r.engCode, set: canEdit ? (r, v) => setF(r, "engCode", v === null ? undefined : String(v)) : undefined },
    { id: "title", title: "Title", width: 320, get: (r) => r.title, set: canEdit ? (r, v) => setF(r, "title", String(v ?? "")) : undefined },
    { id: "defaultWeight", title: "Default weight", width: 110, kind: "number", get: (r) => r.defaultWeight, set: canEdit ? (r, v) => setF(r, "defaultWeight", v) : undefined },
    { id: "chainCode", title: "Doc/DLV chain", width: 110, options: chainCodes, get: (r) => r.chainCode, set: canEdit ? (r, v) => setF(r, "chainCode", v) : undefined, tone: (r) => (r.chainCode && !chainCodes.includes(r.chainCode) ? "warn" : undefined) },
    { id: "procChainCode", title: "Procurement chain", width: 130, options: chainCodes, get: (r) => r.procChainCode, set: canEdit ? (r, v) => setF(r, "procChainCode", v) : undefined, tone: (r) => (r.procChainCode && !chainCodes.includes(r.procChainCode) ? "warn" : undefined) },
    { id: "category", title: "Category", width: 90, get: (r) => r.category, set: canEdit ? (r, v) => setF(r, "category", v === null ? undefined : String(v)) : undefined },
  ];
  const [sel, setSel] = useState<R[]>([]);
  const [q, setQ] = useState("");
  const rows = refs.filter((r) => !q || `${r.discipline ?? ""} ${r.docType} ${r.engCode ?? ""} ${r.title} ${r.chainCode ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  const key = (r: { docType: string; engCode?: string; title: string; chainCode?: string }) => `${r.docType.toUpperCase()}|${(r.engCode ?? "").toUpperCase()}|${r.title.toUpperCase()}|${(r.chainCode ?? "").toUpperCase()}`;
  const build = (mapped: MappedRow[], ctx: { mode?: string }): Preview => {
    const replace = ctx.mode === "replace";
    const existing = new Map(refs.map((r) => [key(r), r]));
    const items = mapped.map((m) => {
      const w = num(m.values.defaultWeight);
      const up = (v: unknown) => str(v as never).toUpperCase() || undefined;
      const it = {
        docType: str(m.values.docType).toUpperCase(),
        engCode: str(m.values.engCode) || undefined,
        discipline: str(m.values.discipline) || undefined,
        title: str(m.values.title),
        defaultWeight: w ?? undefined,
        chainCode: up(m.values.chainCode),
        procChainCode: up(m.values.procChainCode),
        category: str(m.values.category) || undefined,
      };
      const onlyTitle = !it.docType && !it.engCode && !it.discipline && it.defaultWeight === undefined && !it.chainCode && !!it.title;
      const errors = [onlyTitle ? `Only a title ("${it.title}"): probably the rest of the previous row's title that wrapped onto its own line. Skipped; fix it in the file if needed.` : !it.docType && "No doc type", !onlyTitle && !it.title && "No title", str(m.values.defaultWeight) && w === null && `Weight "${str(m.values.defaultWeight)}" isn't a number`].filter(Boolean) as string[];
      const warn = [it.chainCode, it.procChainCode].filter((c) => c && !chainCodes.includes(c)).map((c) => `chain ${c} isn't in the library yet`);
      return { line: m.line, it, errors, warn };
    });
    const seen = new Set<string>();
    const previewRows = items.map(({ line, it, errors, warn }) => {
      const k = key(it);
      const dupe = seen.has(k);
      seen.add(k);
      const ex = replace ? undefined : existing.get(k);
      const same = ex && (["discipline", "defaultWeight", "procChainCode", "category"] as const).every((f) => (ex[f] ?? undefined) === (it[f] ?? undefined));
      const detail = [it.title, it.defaultWeight !== undefined ? `${it.defaultWeight} h` : "", it.chainCode ?? "", ...warn, dupe ? "same doc type, eng code, title and chain as an earlier row; the later row wins" : ""].filter(Boolean).join(" · ");
      return {
        line,
        key: `${it.docType} ${it.engCode ?? ""}`.trim() || "(blank)",
        status: (errors.length ? "error" : ex ? (same ? "unchanged" : "changed") : warn.length || dupe ? "warning" : "new") as "error",
        detail: errors.length ? errors.join("; ") : detail,
      };
    });
    const good = items.filter((x) => !x.errors.length).map((x) => x.it);
    return {
      problems: [],
      strict: false,
      note: (
        <p className="text-sm text-slate-600">
          {replace ? `Replaces the whole list (${refs.length} entries now) with this file.` : "Rows with the same doc type, eng code, title and chain update the existing entry; others are added."} Chains that aren't in the library yet are kept and show in amber until you add them.
        </p>
      ),
      rows: previewRows,
      commit: async () => {
        const todo = replace ? good : good.filter((it) => {
          const ex = existing.get(key(it));
          return !ex || (["discipline", "defaultWeight", "procChainCode", "category"] as const).some((f) => (ex[f] ?? undefined) !== (it[f] ?? undefined));
        });
        for (let i = 0; i < Math.max(todo.length, replace ? 1 : 0); i += 800) await importRefs({ rows: todo.slice(i, i + 800), replace: replace && i === 0 });
      },
    };
  };
  return (
    <Card
      title="Reference deliverables"
      actions={
        <>
          <ExportButton
            label={refs.length ? "Export" : "Download template"}
            file="reference-deliverables.xlsx"
            sheets={() => [
              {
                name: "Reference deliverables",
                headerRow: 0,
                rows: [REF_FIELDS.map((f) => f.label), ...refs.map((r) => [r.docType, r.engCode ?? null, r.title, r.discipline ?? null, r.defaultWeight ?? null, r.chainCode ?? null, r.procChainCode ?? null, r.category ?? null])],
              },
            ]}
          />
          {canEdit && (
            <MappedImportButton
              fields={REF_FIELDS}
              rememberKey="refs"
              modes={[
                { value: "upsert", label: "Add new and update matching", hint: "Keeps your current list. A row with the same doc type, eng code, title and chain updates that entry." },
                { value: "replace", label: "Replace the whole list", hint: "Deletes the current reference list, then imports this file." },
              ]}
              build={build}
            />
          )}
          {canEdit && (
            <Button
              variant="danger"
              disabled={!sel.length}
              onClick={async () => {
                if (sel.length > 1 && !window.confirm(`Delete ${sel.length} reference deliverables?`)) return;
                const ids = sel.map((r) => r._id);
                let n = 0;
                for (let i = 0; i < ids.length; i += 500) {
                  const r = await run(() => delMany({ ids: ids.slice(i, i + 500) }));
                  if (r === undefined) break;
                  n += r;
                }
                toast({ text: `Deleted ${n}`, tone: "info" });
              }}
            >
              Delete selected
            </Button>
          )}
          {canEdit && (
            <Button variant="primary" onClick={() => run(() => upsert({ docType: "DWG", title: "New reference deliverable" }))}>
              <Plus className="size-4" /> Add
            </Button>
          )}
        </>
      }
    >
      <div className="mb-2 flex items-center gap-3">
        <Input placeholder="Search" aria-label="Search reference deliverables" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" />
        <p className="text-xs text-slate-500">Used to default the chain and weight when adding deliverables from a document type and engineering code. {refs.length} entries.</p>
      </div>
      <DataGrid rows={rows} cols={cols} height={520} onSelectRows={setSel} onEdit={(edits) => edits.forEach((e) => e.col.set?.(e.row, e.value))} onError={(m) => toast({ text: m, tone: "error" })} />
    </Card>
  );
}
